import { useCallback } from 'react';
import uuid from 'uuid/v4';

import { type DocNode } from '@atlaskit/adf-schema';

import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';

import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { usePageSpaceKey } from '@confluence/page-context';

import type { DynamicUiSource } from './useRemixExtension.tsx';

/**
 * Hook that provides a callback to send selected text to the AIFC staging modal
 * as a Rovo chat message, skipping the prompt screen and going straight to the
 * staging area preview.
 *
 * This is used by the Remix feature to hand off option selection
 * to the AIFC Create with Rovo flow.
 */
export const useRemixToAIFC = () => {
	const { sendMessage, createConversationOptimistically } = useSmartCreationChatStoreApi();
	const { setPrompt } = useStagingAreaActions();
	const { openModal } = useSmartCreationModalActions();
	const [spaceKey] = usePageSpaceKey();

	const openAIFCStagingWithPrompt = useCallback(
		async (
			selectedText: string,
			dynamicUiType: string,
			dynamicUiSource: DynamicUiSource | undefined,
		) => {
			let promptText: string = '';
			if (dynamicUiType === 'diagram') {
				promptText = `Create a whiteboard diagram from this content: ${selectedText}`;
			} else if (dynamicUiType === 'database') {
				promptText = `Create a database from this content: ${selectedText}`;
			} else {
				// TODO: Probably need a separate case specifically for the custom input type
				promptText = `Create a ${dynamicUiType} from this content: ${selectedText}`;
			}

			// Build the ADF DocNode prompt
			const prompt: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					{
						type: 'paragraph',
						content: [
							{
								type: 'text',
								text: promptText,
							},
						],
					},
				],
			};

			const newConversationId = uuid();
			await createConversationOptimistically({ id: newConversationId });

			// Send the message to Rovo
			sendMessage({
				prompt,
				storeUserMessages: false,
				conversationId: newConversationId,
				messageSources: {
					interactionSource: 'smartCreate',
				},
				productKey: 'confluence',
				contentContext: 'staging-area',
				minionAlias: 'remix-content',
				creationContextParams: {
					dynamicUiType,
					dynamicUiSource,
				},
			});

			// Store the prompt text for display in the staging area header
			setPrompt(promptText);

			// Open the Confluence Smart Creation Modal with stagingAreaActive=true
			// to skip the prompt screen and go straight to the staging area preview
			// setMessageIdSelectedForPreview(null);
			openModal(
				'dynamicUi', // entryPoint for analytics
				spaceKey ?? undefined, // spaceKey
				undefined, // parentContent
				true, // stagingAreaActive — skips the prompt screen
			);
		},
		[createConversationOptimistically, sendMessage, setPrompt, openModal, spaceKey],
	);

	return { openAIFCStagingWithPrompt };
};
